import { alpha } from '@mui/material';

/**
 * Elegant color palette for dynamic coloring
 * Each color pair has light mode and dark mode variants
 */
export const COLOR_PALETTE = [
    { light: '#6366f1', dark: '#a5b4fc' }, // Indigo
    { light: '#10b981', dark: '#6ee7b7' }, // Emerald
    { light: '#f59e0b', dark: '#fcd34d' }, // Amber
    { light: '#ec4899', dark: '#f9a8d4' }, // Pink
    { light: '#8b5cf6', dark: '#c4b5fd' }, // Violet
    { light: '#06b6d4', dark: '#67e8f9' }, // Cyan
    { light: '#3b82f6', dark: '#93c5fd' }, // Blue
    { light: '#ef4444', dark: '#fca5a5' }, // Red
    { light: '#84cc16', dark: '#bef264' }, // Lime
    { light: '#f97316', dark: '#fdba74' }, // Orange
    { light: '#14b8a6', dark: '#5eead4' }, // Teal
    { light: '#a855f7', dark: '#d8b4fe' }, // Purple
];

/**
 * Simple hash function to convert a string to a consistent number
 * @param str - The string to hash
 * @returns A positive integer hash value
 */
const hashString = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
};

/**
 * Get a consistent color for any string value (e.g., role name, subject name, etc.)
 * Uses hashing to ensure the same string always gets the same color
 * 
 * @param value - The string value to get a color for
 * @param isDark - Whether the theme is in dark mode
 * @returns Object with bg (background) and text colors
 * 
 * @example
 * const colors = getHashedColor('Teacher', true);
 * // { bg: 'rgba(99, 102, 241, 0.15)', text: '#a5b4fc' }
 */
export const getHashedColor = (value: string, isDark: boolean): { bg: string; text: string } => {
    const index = hashString(value) % COLOR_PALETTE.length;
    const colorPair = COLOR_PALETTE[index];
    const baseColor = isDark ? colorPair.dark : colorPair.light;

    return {
        bg: alpha(isDark ? colorPair.light : colorPair.light, isDark ? 0.15 : 0.1),
        text: baseColor,
    };
};

/**
 * Build a name -> color map where two different names never share a palette
 * slot (up to the palette size). Names are sorted alphabetically for stable
 * assignment; each name starts at hash(name) % paletteSize and linear-probes
 * forward if that slot is already taken. Beyond COLOR_PALETTE.length unique
 * names we wrap and collisions become unavoidable.
 */
export const buildHashedColorMap = (
    names: string[],
    isDark: boolean,
): Map<string, { bg: string; text: string }> => {
    const unique = Array.from(new Set(names)).sort();
    const paletteSize = COLOR_PALETTE.length;
    const used = new Set<number>();
    const map = new Map<string, { bg: string; text: string }>();
    for (const name of unique) {
        const start = hashString(name) % paletteSize;
        let slot = start;
        for (let step = 0; step < paletteSize; step++) {
            const candidate = (start + step) % paletteSize;
            if (!used.has(candidate)) { slot = candidate; break; }
        }
        used.add(slot);
        const pair = COLOR_PALETTE[slot];
        map.set(name, {
            bg: alpha(pair.light, isDark ? 0.15 : 0.1),
            text: isDark ? pair.dark : pair.light,
        });
    }
    return map;
};

/**
 * Get a consistent color with just the main color and background
 * Useful for chips, badges, and other UI elements
 *
 * @param value - The string value to get a color for
 * @param isDark - Whether the theme is in dark mode
 * @returns Object with color and bgColor
 */
export const getHashedColorPair = (value: string, isDark: boolean): { color: string; bgColor: string } => {
    const index = hashString(value) % COLOR_PALETTE.length;
    const colorPair = COLOR_PALETTE[index];
    const color = isDark ? colorPair.dark : colorPair.light;

    return {
        color,
        bgColor: alpha(color, isDark ? 0.15 : 0.1),
    };
};
